import { useMutation, useQueryClient } from "react-query";

import axios from "axios";

import { projectServiceEndpoint } from "api/project";

import type { LineStyle } from "components/project/shapefiles/shapefiles.constants";

import { PROJECT_SHAPEFILE_FEATURES_QUERY_KEYS } from "../../queries/useProjectShapefileFeaturesQuery";
import { updateShapefilePropertiesCache } from "../utils/updateShapefilePropertiesCache";

export interface EditableGeomProperties {
  color?: string;
  opacity?: number;
  thickness?: number;
  strokeColor?: string;
  lineStyle?: LineStyle;
  fillPattern?: string;
}
export interface IUseUpdateBatchProjectGeomsMutationParams {
  onError?: (error) => void;
  onMutate?: (variables: IUpdateBatchProjectGeomsParamsT) => void;
  onSettled?: () => void;
  onSuccess?: (data) => void;
}
export interface IUpdateBatchProjectGeomsParamsT {
  shapeFileGeomIds: string[];
  update: EditableGeomProperties;
}

async function UpdateBatchProjectGeoms(params: IUpdateBatchProjectGeomsParamsT) {
  const response = await axios.put(
    `${projectServiceEndpoint}/shapefile-geom/update`,
    params
  );
  return response.data;
}

export function useUpdateBatchProjectGeomsMutation({
  onError,
  onMutate,
  onSettled,
  onSuccess
}: IUseUpdateBatchProjectGeomsMutationParams) {
  const queryClient = useQueryClient();

  return useMutation(
    (params: IUpdateBatchProjectGeomsParamsT) => UpdateBatchProjectGeoms({ ...params }),
    {
      onMutate: async (variables) => {
        // Call user's onMutate if provided
        onMutate && onMutate(variables);
      },
      onSuccess: (data, variables) => {
        updateShapefilePropertiesCache({
          queryClient,
          shapefileGeomIds: variables.shapeFileGeomIds,
          update: variables.update
        });

        // Always invalidate the detail view query (has full geometry)
        queryClient.invalidateQueries({
          queryKey: [PROJECT_SHAPEFILE_FEATURES_QUERY_KEYS.projectShapefileFeatures]
        });

        onSuccess && onSuccess(data);
      },
      onError: (error) => {
        onError && onError(error);
      },
      onSettled: onSettled && onSettled
    }
  );
}
